export interface PurchaseOrder {
  // ===== INFORMAÇÕES BÁSICAS =====
  id: number;
  orderNumber: string;
  requestDate: Date;
  approvalDate?: Date;
  expectedDeliveryDate?: Date;
  actualDeliveryDate?: Date;
  status: string;
  priority: string;

  // ===== VALORES FINANCEIROS =====
  subTotal: number;
  discountAmount?: number;
  shippingCost?: number;
  taxAmount?: number;
  totalAmount: number;

  // ===== RELACIONAMENTOS =====
  requestedByStaffId: number;
  requestedByStaffName?: string;
  approvedByStaffId?: number;
  approvedByStaffName?: string;
  supplierId?: number;
  supplierName?: string;
  costCenterId?: number;
  costCenterName?: string;
  deliveryWarehouseId?: number;
  deliveryWarehouseName?: string;

  // ===== INFORMAÇÕES ADICIONAIS =====
  description?: string;
  deliveryInstructions?: string;
  paymentTerms?: string;
  deliveryAddress?: string;
  supplierOrderNumber?: string;
  invoiceNumber?: string;
  rejectionReason?: string;
  cancellationReason?: string;
  notes?: string;

  // ===== CONTROLE DE APROVAÇÃO =====
  requiresApproval: boolean;
  /** Fluxo completo de compras (API /complete): aprovação pelo gestor designado. */
  requiresManagerApproval?: boolean;
  managerApproverStaffId?: number;
  /** Quando a API enviar nome do gestor (evita GET extra no staff). */
  managerApproverName?: string;
  requiredAdditionalApprovalsCount?: number;
  additionalApprovalsCompleted?: number;
  currentRequiredApprovalOrder?: number;
  totalRequiredApprovalSteps?: number;
  isManagerApprovalCompleted?: boolean;
  isAwaitingCurrentApprovalOrder?: boolean;
  requiredAdditionalApprovers?: unknown[];
  availableApprovalMatrixRules?: unknown[];
  quotationHeaders?: unknown[];
  approvalLevel?: string;
  approvalLimit?: number;

  // ===== PROPRIEDADES ADICIONAIS =====
  totalDiscount?: number;
  totalTax?: number;
  departmentName?: string;
  supplierContact?: string;
  /** Preenchido na listagem para exibir aprovação do gestor (UI). */
  gestorAprovacaoTexto?: string;

  // ===== LISTAS RELACIONADAS =====
  items: PurchaseOrderItem[];
  approvals: PurchaseOrderApproval[];

  // ===== CAMPOS CALCULADOS =====
  daysToDelivery: number;
  isOverdue: boolean;
  isPartiallyReceived: boolean;
  canBeApproved: boolean;
  canBeRejected: boolean;
  canBeCancelled: boolean;

  // ===== ESTATÍSTICAS =====
  totalItems: number;
  approvedItems: number;
  receivedItems: number;
  receiptPercentage: number;
  daysInCurrentStatus: number;
  currentApprovalLevel?: string;
  nextApprovalLevel?: string;

  // ===== CAMPOS BASE =====
  createdAt?: Date;
  createdBy?: string;
  updatedAt?: Date;
  updatedBy?: string;
  isActive: boolean;
}

export interface PurchaseOrderItem {
  // ===== RELACIONAMENTOS =====
  id: number;
  purchaseOrderId: number;
  purchaseOrderNumber?: string;
  itemId?: number;
  itemName?: string;

  // ===== INFORMAÇÕES DO PRODUTO =====
  productName: string;
  productCode?: string;
  brand?: string;
  model?: string;
  specifications?: string;
  unit: string;

  // ===== QUANTIDADES =====
  quantityRequested: number;
  quantityApproved: number;
  quantityReceived: number;
  quantityPending: number;

  // ===== VALORES FINANCEIROS =====
  estimatedUnitPrice?: number;
  actualUnitPrice?: number;
  discountPercentage?: number;
  discountAmount?: number;
  estimatedTotalPrice: number;
  actualTotalPrice: number;

  // ===== STATUS E CONTROLE =====
  status: string;
  rejectionReason?: string;
  expectedDeliveryDate?: Date;
  actualDeliveryDate?: Date;

  // ===== INFORMAÇÕES DE RECEBIMENTO =====
  receivedByStaffId?: number;
  receivedByStaffName?: string;
  receiptDate?: Date;
  batchNumber?: string;
  expirationDate?: Date;
  receiptNotes?: string;

  // ===== INFORMAÇÕES ADICIONAIS =====
  description?: string;
  justification?: string;
  priority?: string;
  category?: string;
  isUrgent: boolean;
  isRecurring: boolean;

  // ===== CONTROLE DE QUALIDADE =====
  qualityStatus?: string;
  qualityNotes?: string;
  qualityCheckedByStaffId?: number;
  qualityCheckedByStaffName?: string;
  qualityCheckDate?: Date;

  // ===== CAMPOS CALCULADOS =====
  remainingQuantity: number;
  receiptPercentage: number;
  isFullyReceived: boolean;
  isPartiallyReceived: boolean;
  isOverdue: boolean;
  unitPriceDifference?: number;
  totalPriceDifference?: number;
  daysToDelivery: number;
  statusDisplayName?: string;
  priorityDisplayName?: string;

  // ===== CAMPOS BASE =====
  createdAt?: Date;
  createdBy?: string;
  updatedAt?: Date;
  isActive: boolean;
}

export interface PurchaseOrderApproval {
  // ===== RELACIONAMENTOS =====
  id: number;
  purchaseOrderId: number;
  purchaseOrderNumber?: string;
  staffId: number;
  staffName?: string;

  // ===== INFORMAÇÕES DA APROVAÇÃO =====
  approvalDate: Date;
  action: string;
  approvalLevel: string;
  comments?: string;
  rejectionReason?: string;
  requestedChanges?: string;
  status: string;
  approverStaffName?: string;
  createdAt?: Date;

  // ===== CONTROLE DE SEQUÊNCIA =====
  approvalOrder: number;
  isFinalApproval: boolean;
  isAutomaticApproval: boolean;

  // ===== INFORMAÇÕES ADICIONAIS =====
  approvalMethod?: string;
  delegatedBy?: string;
  delegationExpiry?: Date;
  approvalConditions?: string;

  // ===== CAMPOS CALCULADOS =====
  isApproved: boolean;
  isRejected: boolean;
  hasRequestedChanges: boolean;
  daysToApprove: number;

  // ===== CAMPOS DE EXIBIÇÃO =====
  actionDisplayName?: string;
  approvalLevelDisplayName?: string;
  approvalMethodDisplayName?: string;
  statusColor?: string;
  statusIcon?: string;
  canEdit: boolean;
  canCancel: boolean;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  isActive: boolean;
}

export interface PurchaseOrderStatusHistory {
  // ===== RELACIONAMENTOS =====
  id: number;
  purchaseOrderId: number;
  purchaseOrderNumber?: string;
  staffId: number;
  staffName?: string;

  // ===== INFORMAÇÕES DO STATUS =====
  statusDate: Date;
  previousStatus: string;
  newStatus: string;
  action: string;
  comments?: string;
  reason?: string;
  status: string;
  changedDate: Date;
  notes?: string;
  changedByStaffName?: string;

  // ===== INFORMAÇÕES ADICIONAIS =====
  systemModule?: string;
  ipAddress?: string;
  isSystemGenerated: boolean;
  isAutomaticTransition: boolean;

  // ===== CAMPOS CALCULADOS =====
  isStatusUpgrade: boolean;
  isStatusDowngrade: boolean;
  durationInPreviousStatus: number;

  // ===== CAMPOS DE EXIBIÇÃO =====
  previousStatusDisplayName?: string;
  newStatusDisplayName?: string;
  actionDisplayName?: string;
  statusChangeType?: string;
  statusColor?: string;
  statusIcon?: string;
  timeInStatus?: string;
  isImportantChange: boolean;
  sequenceNumber: number;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  isActive: boolean;
}

// ===== ENUMS =====
export enum PurchaseOrderStatus {
  SOLICITADO = 'Solicitado',
  EM_APROVACAO = 'Em Aprovação',
  APROVADO = 'Aprovado',
  REJEITADO = 'Rejeitado',
  EM_COMPRA = 'Em Compra',
  ENTREGUE_PARCIAL = 'Entregue Parcialmente',
  ENTREGUE_TOTAL = 'Entregue Totalmente',
  CANCELADO = 'Cancelado'
}

export enum PurchaseOrderPriority {
  BAIXA = 'Baixa',
  NORMAL = 'Normal',
  ALTA = 'Alta',
  URGENTE = 'Urgente'
}

export enum PurchaseOrderItemStatus {
  PENDENTE = 'Pendente',
  APROVADO = 'Aprovado',
  REJEITADO = 'Rejeitado',
  RECEBIDO_PARCIAL = 'Recebido Parcial',
  RECEBIDO_TOTAL = 'Recebido Total'
}

export enum ApprovalAction {
  PENDENTE = 'Pendente',
  APROVADO = 'Aprovado',
  REJEITADO = 'Rejeitado',
  SOLICITADO_ALTERACAO = 'Solicitado Alteração'
}

export enum ApprovalLevel {
  SUPERVISOR = 'Supervisor',
  GERENTE = 'Gerente',
  DIRETOR = 'Diretor',
  PRESIDENTE = 'Presidente',
  AUTOMATICO = 'Automático'
}

export enum ApprovalStatus {
  PENDENTE = 'Pendente',
  APROVADO = 'Aprovado',
  REJEITADO = 'Rejeitado',
  CANCELADO = 'Cancelado'
}

// ===== DTOs DE REQUISIÇÃO =====
/** Payload de criação de pedido – alinhado ao contrato da API (ex.: Postman) */
export interface CreatePurchaseOrderRequest {
  orderNumber?: string;
  requestDate?: string;       // ISO 8601, ex.: "2026-02-11T00:00:00Z"
  status?: string;            // "Solicitado" na criação
  priority: string;
  requestedByStaffId?: number;
  supplierId?: number;
  costCenterId?: number;
  deliveryWarehouseId?: number;
  expectedDeliveryDate?: string; // "YYYY-MM-DD"
  description?: string;
  deliveryInstructions?: string;
  paymentTerms?: string;
  deliveryAddress?: string;
  supplierOrderNumber?: string;
  notes?: string;
  discountAmount?: number;
  shippingCost?: number;
  taxAmount?: number;
  subTotal?: number;
  totalAmount?: number;
  items: CreatePurchaseOrderItemRequest[];
}

export interface CreatePurchaseOrderItemRequest {
  itemId?: number;
  productName: string;
  productCode?: string;
  brand?: string;
  model?: string;
  specifications?: string;
  unit: string;
  quantityRequested: number;
  estimatedUnitPrice?: number;
  expectedDeliveryDate?: string; // "YYYY-MM-DD"
  description?: string;
  justification?: string;
  priority?: string;
  category?: string;
  isUrgent: boolean;
  isRecurring: boolean;
}

export interface ApprovalRequest {
  staffId: number;
  comments?: string;
}

export interface RejectionRequest {
  staffId: number;
  rejectionReason: string;
}

export interface QuantityUpdateRequest {
  quantity: number;
}

export interface ItemReceiptRequest {
  quantityReceived: number;
  receivedByStaffId: number;
  batchNumber?: string;
  expirationDate?: Date;
  notes?: string;
}
